// Read-only presentation of active tariff inputs. Never writes or normalizes project data.
import { dynamicTariffState } from '../../components/config/dynamicTariff';
import { itemizedTariffRate } from '../../components/config/retailTariff';
import { hourlyValues } from '../../components/config/virtualNetBilling';

const number = v => v == null || String(v).trim() === '' || !Number.isFinite(Number(v)) ? null : Number(v);
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function sizingBillingSummary(cfg, isGreek) {
  const t = (el, en) => isGreek ? el : en;
  const fmt = (v, digits = 4) => v == null ? '—' : v.toLocaleString('en-US', { maximumFractionDigits: digits });
  const tariffs = cfg.feasibility?.technical?.tariffs || {};
  const vnb = cfg.btm?.gridInjection?.mode === 'virtual_net_billing';
  const block = cfg.btm?.virtualNetBilling || {};
  const dynamic = dynamicTariffState(cfg).dynamic;
  const itemized = !dynamic && tariffs.rateMode === 'itemized';
  const single = itemized ? tariffs.itemized?.pricingMode === 'single' : tariffs.legacyPricingMode === 'single';
  const share = number(cfg.btm?.retailTariff?.energySharePct);
  const charge = number(block.nettedChargeEurPerMwh);
  const details = tariffs.itemized || {};
  const vat = number(details.vatRatePct);
  const recovered = number(details.vatRecoverablePct) ?? 0;
  const factor = itemized ? (vat == null ? null : 1 + vat / 100 * (1 - recovered / 100)) : 1;
  const net = (price, own = false) => {
    if (!vnb || price == null || charge == null || factor == null || (!own && !itemized && share == null)) return null;
    return itemized || own ? (price - charge / 1000) * factor : price * share / 100 - charge / 1000;
  };
  const monthly = MONTHS.map((month, i) => {
    const source = itemized ? details.monthly : tariffs.monthly;
    const key = source?.[month] ? month : month.slice(0, 3);
    const pair = source?.[key] || {};
    const prices = (single ? ['peak'] : ['peak', 'offPeak']).map(band => number(pair[band]));
    const purchase = itemized ? (single ? ['peak'] : ['peak', 'offPeak']).map(band => itemizedTariffRate(tariffs, key, band)?.effective ?? null) : prices;
    return { label: new Intl.DateTimeFormat(isGreek ? 'el-GR' : 'en-GB', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2025, i, 1))), prices, purchase, netting: prices.map(p => net(p)) };
  });
  const range = values => {
    if (!values.length || values.some(v => v == null)) return t('Δεν έχει συμπληρωθεί', 'Incomplete');
    const low = Math.min(...values), high = Math.max(...values);
    return `${fmt(low)}${Math.abs(high - low) > 1e-9 ? `–${fmt(high)}` : ''} €/kWh`;
  };
  const fee = number(tariffs.dynamic?.supplierFeeEurKwh) ?? 0;
  const dynamicValue = `${tariffs.dynamic?.passNegativePrices ? 'DAM' : 'max(DAM, 0)'} + ${fmt(fee)}${vnb ? ` − ${fmt(charge == null ? null : charge / 1000)}` : ''} €/kWh`;
  const supplies = vnb && Array.isArray(block.supplies) ? block.supplies : [];
  const supplyRows = supplies.map((s, i) => {
    const own = s.tariffMode === 'own';
    const values = hourlyValues(s.loadProfile);
    const raw = s.loadProfile?.values;
    const complete = [8760, 8784].includes(values.length) && Array.isArray(raw) && raw.every(v => number(v) != null && number(v) >= 0);
    const total = complete ? values.reduce((sum, v) => sum + v, 0) : null;
    const ownPrice = number(s.energyPriceEurKwh);
    return { name: s.name || `${t('Παροχή', 'Supply')} ${i + 1}`, share: `${fmt(number(s.sharePct), 2)} %`, total,
      consumption: total == null ? t('Δεν υπάρχει πλήρες ετήσιο προφίλ', 'No complete annual profile') : `${fmt(total, 0)} kWh`,
      tariff: own ? `${t('Δική της τιμή ενέργειας', 'Own energy price')}: ${fmt(ownPrice)} €/kWh` : t('Τιμολόγιο έργου', 'Project tariff'),
      netting: own ? range([net(ownPrice, true)]) : dynamic ? dynamicValue : range(monthly.flatMap(m => m.netting)) };
  });
  return {
    vnb, dynamic, itemized, single, monthly, supplies: supplyRows,
    mode: dynamic ? t('Δυναμικό · ανά ώρα', 'Dynamic · hourly') : `${itemized ? t('Αναλυτική χρέωση', 'Itemized tariff') : t('Συνολική χρέωση', 'Total tariff')} · ${single ? t('μία τιμή την ημέρα', 'single daily rate') : t('αιχμή / εκτός αιχμής', 'peak / off-peak')}`,
    purchase: dynamic ? t('Τιμή DAM + χρέωση προμηθευτή + ρυθμιζόμενες χρεώσεις', 'DAM + supplier fee + regulated charges') : range(monthly.flatMap(m => m.purchase)),
    netting: dynamic ? dynamicValue : range(monthly.flatMap(m => m.netting)),
    share: itemized || dynamic ? t('Από την ενεργή τιμή ενέργειας', 'From the active energy price') : `${fmt(share, 2)} %`,
    charge: `${fmt(charge, 2)} €/MWh`,
    formula: itemized ? t('(Τιμή ενέργειας − χρέωση συμψηφισμού) × συντελεστής μη ανακτήσιμου ΦΠΑ', '(Energy price − netting charge) × non-recoverable VAT factor') : t('Συνολική τιμή × μερίδιο ενέργειας − χρέωση συμψηφισμού / 1.000', 'Total tariff × energy share − netting charge / 1,000'),
    total: supplyRows.length && supplyRows.every(s => s.total != null) ? `${fmt(supplyRows.reduce((sum, s) => sum + s.total, 0), 0)} kWh` : '—',
    allProject: supplies.every(s => s.tariffMode !== 'own'),
  };
}

/** Whether the project gives no purchase price at all: no month with a positive
 * price (a dynamic tariff is priced by the market, so it never counts as missing).
 * Without one the bill saving is zero and every size looks like a loss. */
export function retailPriceMissing(cfg) {
  const summary = sizingBillingSummary(cfg || {}, true);
  if (summary.dynamic) return false;
  return !summary.monthly.flatMap((m) => m.purchase).some((p) => p != null && p > 0);
}
